import { DEFAULT_LANGUAGE, resolveFieldTranslation, resolveRowTranslation } from "./masterTranslations.js"

const isFilledValue = (value) => value !== undefined && value !== null && value !== ""

// Builds a value -> label map for a master data field.
// Covers the field's own options and, when the field points to another master table,
// the options of the referenced column (whose stored values are option ids/uuids).
// Keys stay canonical (option ids / row uuids) so lookups are language independent;
// only the labels they map to are localised for `lng`.
export const buildMasterOptionLabelMap = (field, masterList = [], lng = DEFAULT_LANGUAGE) => {
    const labelMap = new Map()

    const addOptions = (options) => {
        if (!Array.isArray(options)) {
            return
        }
        options.forEach(option => {
            if (!isFilledValue(option)) {
                return
            }
            const optionValue = typeof option === "object" ? (option.value ?? option.label) : option
            const optionLabel = typeof option === "object" ? (option.label ?? option.value) : option
            if (!isFilledValue(optionValue) || labelMap.has(String(optionValue))) {
                return
            }
            // An option labelled with its own id carries no display value and would
            // otherwise shadow the real label coming from the referenced table
            if (String(optionValue) === String(optionLabel)) {
                return
            }
            labelMap.set(String(optionValue), optionLabel)
        })
    }

    addOptions(resolveFieldTranslation(field, lng)?.options)

    if (field?.parentTable && field?.mappedColumn) {
        const parentTable = (Array.isArray(masterList) ? masterList : []).find(master => master?.dataId === field.parentTable)
        // Matched on the canonical label: `mappedColumn` stores the untranslated column name
        const parentField = parentTable?.fields?.find(parentField => parentField?.label === field.mappedColumn)
        addOptions(resolveFieldTranslation(parentField, lng)?.options)

        // A row can be referenced by its uuid, so map it to the label of the referenced column
        const parentRows = Array.isArray(parentTable?.data) ? parentTable.data : []
        parentRows.forEach(row => {
            const rawValue = row?.[field.mappedColumn]
            if (!isFilledValue(row?.uuid) || !isFilledValue(rawValue) || labelMap.has(String(row.uuid))) {
                return
            }
            // The lookup uses the canonical value (an option id of the referenced column,
            // which row translations never touch); only the fallback text is localised
            const localizedValue = resolveRowTranslation(row, parentTable?.fields, lng)?.[field.mappedColumn]
            labelMap.set(String(row.uuid), labelMap.get(String(rawValue)) ?? localizedValue ?? rawValue)
        })
    }

    return labelMap
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const globalLabelMapCache = new WeakMap()

export const isUuidLike = (value) => typeof value === "string" && UUID_PATTERN.test(value.trim())

// Cached rows (from getSingleMasterData) can omit `translations` that the list
// payload still carries. Copy them across by uuid so dropdowns can localise.
export const mergeRowWithListTranslations = (row, listRows = []) => {
    if (!row || row.translations) {
        return row
    }
    const fromList = (Array.isArray(listRows) ? listRows : []).find((item) => (
        isFilledValue(item?.uuid) && String(item.uuid) === String(row.uuid)
    ))
    if (fromList?.translations) {
        return { ...row, translations: fromList.translations }
    }
    return row
}

// Display text for one cell of a master-backed dropdown. Text columns read the
// row translation; id columns (option ids / referenced row uuids) go through the
// label maps, which already localise option labels.
export const getLocalizedMasterCellText = (row, column, columnField, master, masterList = [], lng = DEFAULT_LANGUAGE) => {
    const rowWithTranslations = mergeRowWithListTranslations(row, master?.data)
    const localizedRow = resolveRowTranslation(rowWithTranslations, master?.fields, lng)
    const rawValue = row?.[column]
    const localizedValue = localizedRow?.[column]
    if (isFilledValue(localizedValue) && String(localizedValue) !== String(rawValue) && !isUuidLike(String(localizedValue))) {
        return localizedValue
    }
    return resolveMasterValueLabel(
        rawValue,
        buildMasterOptionLabelMap(columnField, masterList, lng),
        buildGlobalMasterLabelMap(masterList, lng)
    )
}

// Fallback for fields that hold an option id without declaring which table it came from.
// Only uuid shaped values are collected, so a plain value can never be relabelled by accident.
export const buildGlobalMasterLabelMap = (masterList = [], lng = DEFAULT_LANGUAGE) => {
    if (!Array.isArray(masterList)) {
        return new Map()
    }
    // Cached per language: the same list resolves to a different set of labels per locale
    let cachedByLanguage = globalLabelMapCache.get(masterList)
    if (cachedByLanguage?.has(lng)) {
        return cachedByLanguage.get(lng)
    }

    const labelMap = new Map()
    const collectFromFields = (fields) => {
        if (!Array.isArray(fields)) {
            return
        }
        fields.forEach(field => {
            const localizedField = resolveFieldTranslation(field, lng)
            if (Array.isArray(localizedField?.options)) {
                localizedField.options.forEach(option => {
                    const optionValue = typeof option === "object" ? option?.value : option
                    const optionLabel = typeof option === "object" ? (option?.label ?? option?.value) : option
                    if (!isUuidLike(optionValue) || !isFilledValue(optionLabel) || labelMap.has(optionValue)) {
                        return
                    }
                    labelMap.set(optionValue, optionLabel)
                })
            }
            collectFromFields(field?.children)
        })
    }

    masterList.forEach(master => collectFromFields(master?.fields))
    if (!cachedByLanguage) {
        cachedByLanguage = new Map()
        globalLabelMapCache.set(masterList, cachedByLanguage)
    }
    cachedByLanguage.set(lng, labelMap)
    return labelMap
}

export const resolveMasterValueLabel = (value, labelMap, fallbackMap) => {
    if (!isFilledValue(value)) {
        return value
    }
    if (Array.isArray(value)) {
        return value.map(item => resolveMasterValueLabel(item, labelMap, fallbackMap))
    }
    if (typeof value === "object") {
        return value
    }
    // A mapping onto the value itself is not a label, so keep looking
    const lookup = (key) => {
        const fieldLabel = labelMap?.get(key)
        if (isFilledValue(fieldLabel) && String(fieldLabel) !== key) {
            return fieldLabel
        }
        const fallbackLabel = fallbackMap?.get(key)
        if (isFilledValue(fallbackLabel) && String(fallbackLabel) !== key) {
            return fallbackLabel
        }
        return undefined
    }

    // A reference can point at another reference: a field option keyed by the referenced
    // row holds that row's column value, which is itself an option id of that column.
    // Keep resolving while the result is still an id.
    let currentKey = String(value)
    let resolved = value
    const seenKeys = new Set([currentKey])
    while (true) {
        const nextLabel = lookup(currentKey)
        if (nextLabel === undefined) {
            break
        }
        resolved = nextLabel
        const nextKey = String(nextLabel)
        if (!isUuidLike(nextKey) || seenKeys.has(nextKey)) {
            break
        }
        seenKeys.add(nextKey)
        currentKey = nextKey
    }
    return resolved
}

const readOptionLabel = (option) => (option && typeof option === "object" ? (option.label ?? option.value) : option)
const readOptionValue = (option) => (option && typeof option === "object" ? option.value : option)

// Prefer the label stored for `lng` on the field (`translations[lng].options`).
// Matching is by option value so a caller can pass the canonical option object
// and still receive the current-language text. Missing translations fall back
// to the option's own label.
const readLocalizedOptionLabel = (element, option, lng) => {
    const value = readOptionValue(option)
    if (value !== undefined && value !== null) {
        const localizedOptions = resolveFieldTranslation(element, lng)?.options
        if (Array.isArray(localizedOptions)) {
            const match = localizedOptions.find((opt) => String(opt?.value ?? opt) === String(value))
            if (match !== undefined) {
                const localized = readOptionLabel(match)
                if (isFilledValue(localized)) {
                    return localized
                }
            }
        }
    }
    return readOptionLabel(option)
}

// Resolves the display text of an option baked onto a master backed form element.
// Such an option stores canonical ids: `value` is the connected master's row uuid and,
// when the connected column is itself a reference to another master, `label` holds the
// referenced row's uuid rather than display text. Consumers that render `option.label`
// directly (the condition pickers, request summaries) would otherwise show a raw id.
// A label that is already display text resolves to itself, so plain columns are unchanged.
// Regular (non-master) options use the field's translation for `lng` so a set-into-text
// copies the same label the reader sees on the dropdown / radio / multiple choice.
export const buildElementOptionLabelResolver = (element, masterList = [], lng = DEFAULT_LANGUAGE) => {
    const readLabel = (option) => readLocalizedOptionLabel(element, option, lng)
    const readValue = readOptionValue

    if (!element?.parentTable) {
        return readLabel
    }

    const master = (Array.isArray(masterList) ? masterList : []).find(item => item?.dataId === element.parentTable)
    // `mappedColumn` stores the untranslated column name; older elements fall back to their label
    const column = element.mappedColumn || element.label
    const columnField = master?.fields?.find(field => field?.label === column)
    const columnLabelMap = buildMasterOptionLabelMap(columnField, masterList, lng)
    const globalLabelMap = buildGlobalMasterLabelMap(masterList, lng)

    // The row the option was baked from is the authoritative text, and it is localised for
    // the reader, so it wins over the snapshot label the same way DropdownInput resolves it
    const rowLabelMap = new Map()
    const rows = Array.isArray(master?.data) ? master.data : []
    rows.forEach(row => {
        if (!isFilledValue(row?.uuid)) {
            return
        }
        const rawText = resolveRowTranslation(row, master?.fields, lng)?.[column]
        const text = resolveMasterValueLabel(rawText, columnLabelMap, globalLabelMap)
        // An unresolved id carries no display value, so leave the option's own label in place
        if (typeof text === "string" && text !== "" && !isUuidLike(text)) {
            rowLabelMap.set(String(row.uuid), text)
            // A reference column is keyed by the referenced row, so the cell leads to the
            // same text as the row it sits on. Values stored under either id resolve.
            const rawCell = row?.[column]
            if (columnField?.parentTable && isFilledValue(rawCell) && !rowLabelMap.has(String(rawCell))) {
                rowLabelMap.set(String(rawCell), text)
            }
        }
    })

    return (option) => {
        const label = readLabel(option)
        const rowLabel = rowLabelMap.get(String(readValue(option)))
        if (isFilledValue(rowLabel)) {
            return rowLabel
        }
        return isUuidLike(label)
            ? resolveMasterValueLabel(label, columnLabelMap, globalLabelMap)
            : label
    }
}

// Snapshot options for a master column onto a form widget. A column that
// points at another master stores that row's uuid in the cell; resolve it to
// display text here so public forms and share links do not need the live table.
//
// A reference column is keyed by the referenced row's uuid rather than by the row
// it was read from, so the same choice carries the same id whether it is picked on
// the referenced master's own dropdown or on the column that points at it. Several
// rows can reference one row, which is why the ids they were read from travel along
// on `rowUuids`: values stored before this keying are still recognised through them.
export const buildMasterColumnOptions = (tableData, columnLabel, masterList = [], lng = DEFAULT_LANGUAGE) => {
    if (!tableData || !columnLabel) {
        return []
    }
    const currentMasterList = Array.isArray(masterList) ? masterList : []
    const columnField = (Array.isArray(tableData?.fields) ? tableData.fields : []).find((field) => field?.label === columnLabel)
    const columnLabelMap = buildMasterOptionLabelMap(columnField, currentMasterList, lng)
    const globalLabelMap = buildGlobalMasterLabelMap(currentMasterList, lng)
    const resolveLabel = (rawValue, row) => {
        const fromRow = row
            ? getLocalizedMasterCellText(row, columnLabel, columnField, tableData, currentMasterList, lng)
            : resolveMasterValueLabel(rawValue, columnLabelMap, globalLabelMap)
        if (typeof fromRow === "string" && fromRow !== "" && !isUuidLike(fromRow)) {
            return fromRow
        }
        const mapped = resolveMasterValueLabel(rawValue, columnLabelMap, globalLabelMap)
        if (typeof mapped === "string" && mapped !== "" && !isUuidLike(mapped)) {
            return mapped
        }
        return typeof rawValue === "string" ? rawValue : String(rawValue ?? "")
    }

    const isReferenceColumn = !!columnField?.parentTable

    const rawOptions = []
    ;(Array.isArray(tableData?.data) ? tableData.data : []).forEach((row) => {
        const val = row?.[columnLabel]
        if (val === undefined || val === null || val === "") {
            return
        }
        const rowUuids = row?.uuid ? [String(row.uuid)] : []
        if (Array.isArray(val)) {
            val.forEach((item) => {
                if (item === undefined || item === null || item === "") {
                    return
                }
                rawOptions.push({ label: resolveLabel(item, row), value: String(item), rowUuids })
            })
            return
        }
        rawOptions.push({
            label: resolveLabel(val, row),
            value: isReferenceColumn ? String(val) : (row.uuid || String(val)),
            rowUuids
        })
    })

    return rawOptions.reduce((accumulator, current) => {
        if (!current?.label || !current?.value) {
            return accumulator
        }
        const existing = accumulator.find((item) => item.label === current.label)
        if (existing) {
            // One label, several source rows: the ids they were read from all stand
            // for this option, so none of them is lost when the duplicate is dropped.
            current.rowUuids.forEach((uuid) => {
                if (!existing.rowUuids.includes(uuid)) existing.rowUuids.push(uuid)
            })
            return accumulator
        }
        return accumulator.concat({ ...current, rowUuids: [...current.rowUuids] })
    }, []).map((option) => (
        // Nothing to recognise when the option is already keyed by its own row.
        option.rowUuids.length === 1 && option.rowUuids[0] === String(option.value)
            ? { label: option.label, value: option.value }
            : option
    ))
}

export const getUniqueValuesFromArray = (arr, key) => {
    const uniqueArray = arr.reduce((accumulator, current) => {
        const x = accumulator.find(item => item[key] === current[key]);
        if (!x) {
            return accumulator.concat([current]);
        } else {
            return accumulator;
        }
    }, []);
    return uniqueArray
}

export const getTopPositionOfSprint = (sprints, currentSprintId, parentId, isSubLevel) => {
    let reqPosition, marginTop = 16, blockHeight = 36;
    if (sprints.length === 1 && !isSubLevel) {
        return 0
    }
    let currentParentSprintsIndex = sprints.findIndex(sprint => sprint.uuid === parentId || sprint.uuid === currentSprintId)
    let currentParentSprints = sprints.filter((sprint, index) => index <= currentParentSprintsIndex);
    let sprintsCounts = currentParentSprints.length;
    let totalSublevelsCount = 0;
    if (sprintsCounts > 1) {
        currentParentSprints.forEach(sprint => {
            if (sprint.subLevels) {
                totalSublevelsCount += sprint.subLevels.length
            }
        })
    } else {

    }
    let currentSprint = currentParentSprints[currentParentSprints.length - 1]
    if (isSubLevel) {
        let currentSubLevelIndex = currentSprint.subLevels.findIndex(currentSprint => currentSprint.uuid === currentSprintId)
        totalSublevelsCount = totalSublevelsCount - (currentSprint.subLevels.length - currentSubLevelIndex) + 1
    }
    reqPosition = (totalSublevelsCount * (marginTop + blockHeight)) + ((sprintsCounts - 1) * (39.6 + 12))
    return reqPosition
}

export const getSprintTopPosition = (sprints, currentSprintId, isSubLevel) => {
    let topPosition = 0;
    for (let i = 0; i < sprints.length; i++) {
        let parentSprint = sprints[i], foundSubLevel = false;
        if (i > 0) {
            topPosition += 24
        }
        if (!isSubLevel && parentSprint.uuid === currentSprintId) {
            // topPosition += 12
            // if(i > 0) {
            //     topPosition += 32
            // }
            break;
        }
        if (parentSprint.subLevels?.length) {
            topPosition += 32
            for (let j = 0; j < parentSprint.subLevels.length; j++) {
                let subLevelSprint = parentSprint.subLevels[j]
                topPosition += 16
                if (subLevelSprint.uuid === currentSprintId) {
                    foundSubLevel = true
                    // if(i > 0) {
                    //     topPosition += 16

                    // }
                    break
                }
                topPosition += 24
            }
        }
        if (foundSubLevel) {
            break;
        }
    }
    return topPosition
}

export const getRateForSelectedSkill = (deliveryUnit, teamObj, masterData) => {
    let reqDeliveryUnit = deliveryUnit.split(" - ")
    let reqRateData = {}
    let reqRate = masterData.find(data => {
        let isFound = false
        if (data.region === reqDeliveryUnit[0] && data.country === reqDeliveryUnit[1] && data.location === reqDeliveryUnit[2]) {
            if (data["team"] === teamObj["Team"] && data['skillLevel1'] === teamObj['Skill Level1']) {

                if (teamObj?.["Skill Level2"] && teamObj["Skill Level2"] === data["skillLevel2"]) {

                    if (teamObj?.["Employee Level"] && teamObj["Employee Level"] == data["employeeLevel"]) {

                        isFound = true
                    }

                }
            }
        }
        return isFound
    })
    if (reqRate) {
        Object.keys(reqRate).forEach(key => {
            if (key.includes("onsite") || key.includes("offshore")) {
                reqRateData[key] = reqRate[key]
            }
        })
    }
    return reqRateData
}

export const convertDbDataToFormattedResource = (teamsData) => {
    let teamObj = {}
    let reqTeams = [...teamsData]
    reqTeams.forEach(team => {
        let curTeams = teamObj[team["Team"]] || []
        let curTeamObj = {uuid: team["uuid"], "Skill Level1": team["SkillLevel1"], "Skill Level2": team["SkillLevel2"], "Employee Level": team["EmployeeLevel"], rate: team.rate, calculatedCosts: team.calculatedCosts}
        let reqCount = {}
        for(const key in team.count){
            reqCount[key] = {...team.count[key], cost: team.calculatedCosts[key] || 0}
        }
        curTeamObj.count = reqCount
        curTeams = curTeams.concat(curTeamObj)
        teamObj[team["Team"]] = curTeams
    })
    return teamObj
}